import {
  ACESFilmicToneMapping,
  AdditiveBlending,
  AmbientLight,
  BackSide,
  Box3,
  BoxGeometry,
  CircleGeometry,
  Color,
  ConeGeometry,
  DirectionalLight,
  FogExp2,
  Group,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  PointLight,
  Points,
  RingGeometry,
  Scene,
  SphereGeometry,
  TorusGeometry,
  Vector2,
  Vector3,
  WebGLRenderer,
  type Texture,
} from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { SSAOPass } from 'three/addons/postprocessing/SSAOPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'

import type {
  CharacterKind,
  HazardKind,
  PickupKind,
  PlayerId,
  PropKind,
} from '@/components'
import { clamp, damp } from '@/core/math/mathUtils'
import { Rng } from '@/core/Rng'
import type { ArenaDef, ArenaState } from '@/game/arenas/Arena'
import type { Settings } from '@/game/resources'
import { AssetLibrary } from './assets/AssetLoader'
import { GradePass } from './postfx/GradePass'
import { ArenaMesh } from './procedural/arenaMesh'
import { CharacterRig } from './procedural/character'
import { FighterAura } from './FighterAura'
import { OwnMarker } from './OwnMarker'
import { ModelFighter } from './ModelFighter'
import { createProp, type PropTextures } from './procedural/props'
import {
  makeBarkTexture,
  makeConcreteTexture,
  makeRadialTexture,
  makeRoughnessTexture,
  makeRustTexture,
} from './procedural/textures'
import type { CharacterVisualState, SceneBridge } from './SceneBridge'
import { Vfx } from './Vfx'

const CAMERA_ELEVATION = (52 * Math.PI) / 180
const CAMERA_FOV = 42
/** How far the auto-framing may deviate from the base shot, as a fraction. */
const FRAME_LIMIT = 0.15
/**
 * Visual scale of a fighter. The collider is 0.62 m in radius; at 1.5 the rig
 * stands about 1.8 m tall, which is a person rather than the 20-pixel token the
 * first screenshots showed.
 */
const CHARACTER_SCALE = 1.95
/** Height an imported fighter is fitted to, in metres. */
const FIGHTER_HEIGHT = 2.6

interface EffectVisual {
  object: Object3D
  update(progress: number, intensity: number, dt: number): void
}

/**
 * Scales an imported model so it stands `height` metres tall and rests on the
 * floor. Asset packs are authored at wildly different scales — centimetres,
 * inches, arbitrary units — and a fighter that turns out to be forty metres
 * tall is not a judgement call anybody should have to make by eye.
 */
function fitToHeight(object: Object3D, height: number): void {
  // The matrices have to be current before anything is measured: a freshly
  // cloned model has not been through a render pass, and an unmeasured
  // skinned mesh reports its bind-pose geometry in local units.
  object.updateWorldMatrix(true, true)
  // precise walks the vertex data instead of trusting cached bounding boxes;
  // a skinned mesh's cached box is in bind space and lies about its size.
  const box = new Box3().setFromObject(object, true)
  const size = box.getSize(new Vector3())
  if (!Number.isFinite(size.y) || size.y < 1e-4) {
    console.warn('fighter model has no measurable height; left at its own scale')
    return
  }
  object.scale.multiplyScalar(height / size.y)
  object.updateWorldMatrix(true, true)
  // Re-measure after scaling and sit the lowest point on y = 0.
  const scaled = new Box3().setFromObject(object, true)
  object.position.y -= scaled.min.y
}

interface RegistryEntry {
  object: Object3D
  rig?: CharacterRig
  model?: ModelFighter
  aura?: FighterAura
  marker?: OwnMarker
  effect?: EffectVisual
  light?: PointLight
}

/**
 * Owns the WebGL side of the game and implements `SceneBridge`, the only
 * interface the simulation is allowed to touch.
 *
 * Everything visible is generated here at startup — textures, character rigs,
 * props, the platform itself — so the game ships without a single binary asset
 * while still getting shadows, bloom and a graded, foggy Zone look.
 */
export class Renderer implements SceneBridge {
  readonly scene = new Scene()
  readonly camera: PerspectiveCamera
  readonly vfx: Vfx
  /** Optional external models; empty in 1.0, see `gfx/assets/AssetLoader.ts`. */
  readonly assets = new AssetLibrary()

  private readonly renderer: WebGLRenderer
  private readonly composer: EffectComposer
  private readonly bloom: UnrealBloomPass
  private readonly grade: GradePass
  private readonly ssao: SSAOPass
  private readonly renderPass: RenderPass

  private readonly registry = new Map<number, RegistryEntry>()
  private nextId = 1

  private readonly textures: PropTextures & { glow: Texture; spark: Texture }
  private readonly sun: DirectionalLight
  private readonly hemi: HemisphereLight
  private readonly ambient: AmbientLight
  private readonly dome: Mesh
  private readonly concreteCache = new Map<string, Texture>()
  private arenaMesh: ArenaMesh | null = null
  private arenaDef: ArenaDef | null = null

  private readonly fitCamera = new PerspectiveCamera(CAMERA_FOV, 1.777, 0.5, 420)
  private readonly fitPoint = new Vector3()
  private baseDistance = 30
  /** World-space Z nudge that centres the platform vertically on screen. */
  private baseShiftZ = 0
  private frameOffsetX = 0
  private frameOffsetZ = 0
  private frameZoom = 1
  private shakeAmount = 0
  private shakeSeed = new Rng(0x5eed)
  private impact = 0
  private resizeTimer: number | null = null

  constructor(canvas: HTMLCanvasElement, settings: Settings) {
    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
    })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    // three.js validates every compiled program by default, and the call it
    // uses to do so — `getProgramInfoLog` — blocks until the driver has
    // finished linking: measured at 90–213 ms per program on this scene. A
    // shader that fails now shows up as a black material instead of a console
    // message, which is a trade `shotcheck` covers: every scene is screenshotted
    // on a real GPU before anything ships.
    this.renderer.debug.checkShaderErrors = false
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = PCFSoftShadowMap
    this.renderer.toneMapping = ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.18

    this.scene.background = new Color(0x0d1218)
    this.scene.fog = new FogExp2(0x131a20, 0.013)

    this.camera = new PerspectiveCamera(
      CAMERA_FOV,
      window.innerWidth / window.innerHeight,
      0.5,
      420,
    )

    // ── textures ─────────────────────────────────────────────────────────
    this.textures = {
      concrete: makeConcreteTexture(0x6f6f63, { seed: 7, repeat: 3, grime: 0.38 }),
      rust: makeRustTexture(0x77705f, 91),
      bark: makeBarkTexture(303),
      roughness: makeRoughnessTexture(515, 0.7),
      glow: makeRadialTexture(1, 0, 2.4),
      spark: makeRadialTexture(1, 0, 1.6),
    }

    // ── lighting ─────────────────────────────────────────────────────────
    // A key light, a hemisphere fill and a flat ambient floor. Values here are
    // placeholders: `applyLighting` overwrites all three from the arena's
    // profile, which is what makes the four maps look like four places.
    this.sun = new DirectionalLight(0xbcd2e8, 3.4)
    this.sun.position.set(-22, 34, 16)
    this.sun.castShadow = true
    this.sun.shadow.mapSize.set(2048, 2048)
    this.sun.shadow.bias = -0.0006
    this.sun.shadow.normalBias = 0.02
    this.scene.add(this.sun, this.sun.target)

    this.hemi = new HemisphereLight(0x51707f, 0x1d2016, 1.35)
    this.ambient = new AmbientLight(0x2c3640, 0.85)
    this.scene.add(this.hemi, this.ambient)

    // A faint dome so the horizon is not pure void — it catches the fog.
    this.dome = new Mesh(
      new SphereGeometry(220, 24, 16),
      new MeshBasicMaterial({ color: 0x1b2530, side: BackSide, fog: false }),
    )
    this.scene.add(this.dome)

    this.vfx = new Vfx(this.textures.spark)
    this.scene.add(this.vfx.group)

    // ── post-processing ──────────────────────────────────────────────────
    this.composer = new EffectComposer(this.renderer)
    this.renderPass = new RenderPass(this.scene, this.camera)
    this.composer.addPass(this.renderPass)

    this.ssao = new SSAOPass(this.scene, this.camera, window.innerWidth, window.innerHeight)
    this.ssao.kernelRadius = 6
    this.ssao.minDistance = 0.004
    this.ssao.maxDistance = 0.12
    this.ssao.enabled = settings.ssao
    this.composer.addPass(this.ssao)

    this.bloom = new UnrealBloomPass(
      new Vector2(window.innerWidth, window.innerHeight),
      0.62,
      0.55,
      0.68,
    )
    this.bloom.enabled = settings.bloom
    this.composer.addPass(this.bloom)

    this.grade = new GradePass()
    this.composer.addPass(this.grade)
    this.composer.addPass(new OutputPass())

    this.resize()
    window.addEventListener('resize', this.resize)
  }

  // ─────────────────────────────────────────────────────────────── arena ──

  buildArena(def: ArenaDef): void {
    if (this.arenaMesh) {
      this.scene.remove(this.arenaMesh.group)
      this.arenaMesh.dispose()
    }
    this.arenaDef = def
    this.applyLighting(def)

    this.arenaMesh = new ArenaMesh(def, this.concreteFor(def), this.textures.roughness)
    this.scene.add(this.arenaMesh.group)

    const extent = Math.max(def.extentX, def.extentZ)
    this.sun.target.position.set(def.centerX, 0, def.centerZ)
    const shadow = this.sun.shadow.camera
    shadow.left = -extent * 1.5
    shadow.right = extent * 1.5
    shadow.top = extent * 1.5
    shadow.bottom = -extent * 1.5
    shadow.near = 1
    shadow.far = 120
    shadow.updateProjectionMatrix()

    this.computeBaseDistance(def)
    this.frameOffsetX = 0
    this.frameOffsetZ = 0
    this.frameZoom = 1
    this.applyCamera(0)
  }

  updateArena(arena: ArenaState, dt: number): void {
    this.arenaMesh?.update(arena, dt)
  }

  /** Restores every fallen slab. Called at the start of each round. */
  resetArena(): void {
    this.arenaMesh?.reset()
  }

  /** Swaps the whole light rig, fog and exposure to this arena's profile. */
  private applyLighting(def: ArenaDef): void {
    const l = def.lighting

    this.sun.color.setHex(l.sunColor)
    this.sun.intensity = l.sunIntensity
    this.sun.position.set(...l.sunPosition)

    this.hemi.color.setHex(l.skyColor)
    this.hemi.groundColor.setHex(l.bounceColor)
    this.hemi.intensity = l.hemiIntensity

    this.ambient.color.setHex(l.ambientColor)
    this.ambient.intensity = l.ambientIntensity

    const fog = this.scene.fog as FogExp2
    fog.color.setHex(l.fogColor)
    fog.density = l.fogDensity
    ;(this.scene.background as Color).setHex(l.background)
    ;(this.dome.material as MeshBasicMaterial).color.setHex(l.domeColor)

    this.renderer.toneMappingExposure = l.exposure
  }

  /** Concrete texture for an arena, generated once and cached by id. */
  private concreteFor(def: ArenaDef): Texture {
    const cached = this.concreteCache.get(def.id)
    if (cached) return cached
    const texture = makeConcreteTexture(def.surface.tint, {
      seed: def.surface.seed,
      repeat: def.surface.repeat,
      grime: def.surface.grime,
      rust: def.surface.rust,
    })
    this.concreteCache.set(def.id, texture)
    return texture
  }

  /**
   * Distance at which the whole footprint fits the viewport. Recomputed on
   * resize so a narrow window pulls the camera back instead of cropping the
   * arena — losing sight of a player is a lost round in a game like this.
   */
  private computeBaseDistance(def: ArenaDef): void {
    // Solved numerically rather than with a closed-form guess. A tilted camera
    // foreshortens the ground plane unevenly, and every analytic approximation
    // tried here either cropped the near edge off the bottom of the screen or
    // wasted a quarter of the frame. Projecting the actual corners and scaling
    // the distance until they fit cannot get that wrong.
    const probe = this.fitCamera
    probe.fov = CAMERA_FOV
    probe.aspect = this.camera.aspect
    probe.near = 0.5
    probe.far = 420

    // Fit the actual footprint, not its bounding box. A circle inscribed in its
    // bounding square wastes 21 % of the area, and the cross-shaped arena wastes
    // far more — both were being framed as if their empty corners mattered.
    // Two point sets with two different promises: the ground must sit
    // comfortably inside the frame, while the tops of tall props on the rim
    // merely must not be cut off. Holding both to the same margin reserved a
    // column of empty air above the far edge and shrank the arena on screen.
    const ground: Array<[number, number, number]> = []
    const tall: Array<[number, number, number]> = []
    const steps = 26
    for (let i = 0; i <= steps; i++) {
      for (let j = 0; j <= steps; j++) {
        const x = def.centerX + (i / steps - 0.5) * 2 * def.extentX
        const z = def.centerZ + (j / steps - 0.5) * 2 * def.extentZ
        const d = def.shape.distance(x, z)
        if (d > 0.6) continue
        ground.push([x, 0, z])
        // Only the rim carries tall scenery worth reserving room for.
        if (d > -2.5) tall.push([x, 3, z])
      }
    }
    if (ground.length === 0) ground.push([def.centerX, 0, def.centerZ])

    const place = (dist: number, shift: number): void => {
      probe.position.set(
        def.centerX,
        dist * Math.sin(CAMERA_ELEVATION),
        def.centerZ + shift + dist * Math.cos(CAMERA_ELEVATION),
      )
      probe.lookAt(def.centerX, 0.8, def.centerZ + shift)
      probe.updateProjectionMatrix()
      probe.updateMatrixWorld(true)
    }

    let dist = Math.max(def.extentX, def.extentZ) * 2.2
    let shift = 0

    // Distance and vertical centring interact, so they are solved together.
    // Centring matters because perspective is asymmetric here: the near edge of
    // the platform subtends a much larger angle than the far one, so aiming at
    // the arena's world centre leaves a band of empty sky above it.
    for (let outer = 0; outer < 3; outer++) {
      for (let i = 0; i < 6; i++) {
        place(dist, shift)
        const worstGround = this.worstNdc(ground, probe)
        const worstTall = this.worstNdc(tall, probe)
        // Ground fills 95 % of the frame; tall props may reach the very edge.
        const need = Math.max(worstGround / 0.95, worstTall / 1)
        dist *= need
        if (Math.abs(need - 1) < 0.003) break
      }

      // Measure how a Z nudge moves the footprint on screen, then solve for the
      // nudge that puts its vertical midpoint at the centre of the frame.
      place(dist, shift)
      const centre0 = this.verticalCentre(ground, probe)
      const probeShift = def.extentZ * 0.15
      place(dist, shift + probeShift)
      const centre1 = this.verticalCentre(ground, probe)

      const response = (centre1 - centre0) / probeShift
      if (Math.abs(response) < 1e-6) break
      const correction = -centre0 / response
      shift += Math.max(-def.extentZ, Math.min(def.extentZ, correction))
      if (Math.abs(centre0) < 0.005) break
    }

    this.baseDistance = dist * def.cameraZoom
    this.baseShiftZ = shift
  }

  /** Midpoint between the highest and lowest projected points, in NDC. */
  private verticalCentre(
    points: Array<[number, number, number]>,
    probe: PerspectiveCamera,
  ): number {
    let min = Infinity
    let max = -Infinity
    for (const [x, y, z] of points) {
      this.fitPoint.set(x, y, z).project(probe)
      min = Math.min(min, this.fitPoint.y)
      max = Math.max(max, this.fitPoint.y)
    }
    return (min + max) / 2
  }

  /** Largest absolute NDC coordinate among the given world points. */
  private worstNdc(
    points: Array<[number, number, number]>,
    probe: PerspectiveCamera,
  ): number {
    let worst = 0
    for (const [x, y, z] of points) {
      this.fitPoint.set(x, y, z).project(probe)
      worst = Math.max(worst, Math.abs(this.fitPoint.x), Math.abs(this.fitPoint.y))
    }
    return worst
  }

  // ────────────────────────────────────────────────────────────── camera ──

  /**
   * Gentle auto-framing. The camera never rotates and never leaves the base
   * shot by more than `FRAME_LIMIT`; it only breathes toward the midpoint of
   * the two fighters, which adds life without ever disorienting a player.
   */
  /** Frames the bounding box of the fighters still in play. */
  frameTargets(
    minX: number,
    minZ: number,
    maxX: number,
    maxZ: number,
    dt: number,
    staticCamera: boolean,
  ): void {
    if (!this.arenaDef) return
    const def = this.arenaDef

    let targetX = 0
    let targetZ = 0
    let targetZoom = 1

    if (!staticCamera) {
      const midX = (minX + maxX) / 2 - def.centerX
      const midZ = (minZ + maxZ) / 2 - def.centerZ
      const spread = Math.hypot(maxX - minX, maxZ - minZ)
      const extent = Math.max(def.extentX, def.extentZ)

      targetX = clamp(midX * 0.35, -extent * FRAME_LIMIT, extent * FRAME_LIMIT)
      targetZ = clamp(midZ * 0.35, -extent * FRAME_LIMIT, extent * FRAME_LIMIT)
      // Close quarters pull in a little; a long-range standoff pushes out.
      targetZoom = clamp(0.9 + spread / (extent * 2.6), 1 - FRAME_LIMIT, 1)
    }

    this.frameOffsetX = damp(this.frameOffsetX, targetX, 0.9, dt)
    this.frameOffsetZ = damp(this.frameOffsetZ, targetZ, 0.9, dt)
    this.frameZoom = damp(this.frameZoom, targetZoom, 0.9, dt)
  }

  addShake(strength: number, scale: number): void {
    this.shakeAmount = Math.min(1.4, this.shakeAmount + strength * scale)
    this.impact = Math.min(1, this.impact + strength * 0.5 * scale)
  }

  private applyCamera(dt: number): void {
    if (!this.arenaDef) return
    const def = this.arenaDef
    const dist = this.baseDistance * this.frameZoom
    const cx = def.centerX + this.frameOffsetX
    const cz = def.centerZ + this.frameOffsetZ + this.baseShiftZ

    // Traumatic shake: amplitude falls off quadratically, so big hits punch and
    // small ones only ripple.
    const trauma = this.shakeAmount * this.shakeAmount
    const sx = this.shakeSeed.spread(1) * trauma * 0.85
    const sy = this.shakeSeed.spread(1) * trauma * 0.6
    const roll = this.shakeSeed.spread(1) * trauma * 0.021

    this.camera.position.set(
      cx + sx,
      dist * Math.sin(CAMERA_ELEVATION) + sy,
      cz + dist * Math.cos(CAMERA_ELEVATION),
    )
    this.camera.lookAt(cx, 0.8, cz)
    this.camera.rotation.z += roll

    this.shakeAmount = Math.max(0, this.shakeAmount - dt * 2.4)
    this.impact = Math.max(0, this.impact - dt * 3.2)
  }

  // ────────────────────────────────────────────────────── SceneBridge API ──

  addCharacter(_player: PlayerId, color: number, kind: CharacterKind = 'guard'): number {
    // A registered model wins over the procedural rig. It is posed by position
    // and facing only — see `AssetLibrary.registerFighter`.
    const imported = this.assets.instantiateFighter(kind, color)
    if (imported) {
      const fighter = new ModelFighter(imported, this.assets.fighterClips_(kind))
      // Every pack authors at its own scale, so the model is measured and fitted
      // to the same height the procedural rig stands at rather than trusted.
      if (!this.assets.fighterIsFitted(kind)) fitToHeight(fighter.group, FIGHTER_HEIGHT)
      // The holder stays at unit scale. Anything parented to the fitted group
      // inherits its scale factor, and that factor can be in the hundreds — the
      // first version put the ground glow inside it and turned every modelled
      // fighter into a floodlight.
      const holder = new Group()
      holder.add(fighter.group)
      // The colour still has to be findable at a glance, and a modelled fighter
      // has no coloured ground glow of its own.
      const glow = new Mesh(
        new PlaneGeometry(2.4, 2.4),
        new MeshBasicMaterial({
          map: this.textures.glow,
          color,
          transparent: true,
          opacity: 0.6,
          blending: AdditiveBlending,
          depthWrite: false,
          toneMapped: false,
        }),
      )
      glow.rotation.x = -Math.PI / 2
      glow.position.y = 0.03
      holder.add(glow)

      // The stance dome and the pickup bubble used to belong to the procedural
      // rig, so swapping every fighter to a model silently deleted them — one of
      // the five buttons stopped having any visual whatsoever.
      const aura = new FighterAura()
      holder.add(aura.group)
      const marker = new OwnMarker(this.textures.glow)
      holder.add(marker.group)

      this.scene.add(holder)
      return this.register({ object: holder, model: fighter, aura, marker })
    }

    const rig = new CharacterRig(color, this.textures.glow, kind)
    // The rig is authored at roughly 1.2 m; a fighter needs to be a person on
    // screen, not a token. Scaled to match the collider radius below.
    rig.group.scale.setScalar(CHARACTER_SCALE)
    // Each fighter carries their own light: it separates them from the murk and
    // makes the arena read differently depending on where they stand.
    //
    // Dimmed from 11 over 13 m. At that strength the light sat *inside* the
    // fighter and blew the body out to white, and bloom then smeared the result
    // into a glowing blob with no legs — which is no way to judge a walk cycle.
    // The colour identity is carried by the ground glow, not by the flare.
    const light = new PointLight(color, 4, 9, 2.2)
    light.position.y = 1.1
    rig.group.add(light)
    const marker = new OwnMarker(this.textures.glow)
    // Undo the rig's scale: the mark is sized in metres like on a model.
    marker.group.scale.setScalar(1 / CHARACTER_SCALE)
    rig.group.add(marker.group)
    this.scene.add(rig.group)
    return this.register({ object: rig.group, rig, light, marker })
  }

  addProp(kind: PropKind, seed: number, scale: number): number {
    // A registered external model wins over the procedural mesh; colliders are
    // unaffected either way, so swapping art can never change the physics.
    const object = this.assets.instantiateProp(kind) ?? createProp(kind, seed, this.textures)
    object.scale.multiplyScalar(scale)
    this.scene.add(object)
    return this.register({ object })
  }

  addVortexField(radius: number, color: number): number {
    const effect = createVortexVisual(radius, color, this.textures.glow)
    this.scene.add(effect.object)
    return this.register({ object: effect.object, effect })
  }

  addHazard(kind: HazardKind, radius: number): number {
    const effect = createHazardVisual(kind, radius, this.textures.glow)
    this.scene.add(effect.object)
    return this.register({ object: effect.object, effect })
  }

  addBolt(color: number): number {
    const effect = createBoltVisual(color, this.textures.glow)
    this.scene.add(effect.object)
    return this.register({ object: effect.object, effect })
  }

  addPickup(kind: PickupKind): number {
    const effect = createPickupVisual(kind, this.textures.glow)
    this.scene.add(effect.object)
    return this.register({ object: effect.object, effect })
  }

  addShockwave(color: number): number {
    const effect = createShockwaveVisual(color)
    this.scene.add(effect.object)
    return this.register({ object: effect.object, effect })
  }

  remove(id: number): void {
    const entry = this.registry.get(id)
    if (!entry) return
    this.scene.remove(entry.object)
    entry.rig?.dispose()
    entry.model?.dispose()
    entry.aura?.dispose()
    entry.marker?.dispose()
    entry.light?.dispose()
    // Props and effects allocate their own materials — and the effect factories
    // their own geometry — per instance. A round tears down and rebuilds every
    // prop, so without this a best-of-5 leaked hundreds of materials, each one
    // holding a compiled shader program alive until the context died.
    disposeObject(entry.object, entry.rig !== undefined)
    this.registry.delete(id)
  }

  setTransform(id: number, x: number, y: number, z: number, angle: number): void {
    const entry = this.registry.get(id)
    if (!entry) return
    entry.object.position.set(x, y, z)
    entry.object.rotation.y = angle
  }

  setVisible(id: number, visible: boolean): void {
    const entry = this.registry.get(id)
    if (entry) entry.object.visible = visible
  }

  setScale(id: number, scale: number): void {
    const entry = this.registry.get(id)
    if (entry) entry.object.scale.setScalar(scale)
  }

  updateCharacter(id: number, state: CharacterVisualState, dt: number): void {
    const entry = this.registry.get(id)
    entry?.rig?.update(state, dt)
    entry?.model?.update(state, dt)
    entry?.aura?.update(state, dt)
    entry?.marker?.update(state, dt)
  }

  updateEffect(id: number, progress: number, intensity: number, dt: number): void {
    this.registry.get(id)?.effect?.update(progress, intensity, dt)
  }

  private register(entry: RegistryEntry): number {
    const id = this.nextId++
    this.registry.set(id, entry)
    return id
  }

  /**
   * Removes every registered object the caller did not report as still alive.
   *
   * The render sync used to remember only the ids it had *seen* on a previous
   * frame and delete the ones that disappeared from that set. Anything created
   * and destroyed between two presentation passes was therefore never seen and
   * never deleted — and the plasma bolt does exactly that whenever it is fired
   * point blank or over the edge, because the projectile system runs in the same
   * fixed step that spawns it. Measured on six fighters: ~15 leaked objects per
   * minute, each with its own point light, and every new light count makes
   * three.js recompile every lit material in the scene — 90–213 ms of blocking
   * `getProgramInfoLog` per program. Over two minutes that came to 27 s of
   * frozen frames: 22.6 % of the match.
   *
   * Sweeping the registry itself has no such blind spot: an object that no
   * living entity claims this frame is gone this frame.
   */
  /** Scene objects the bridge is holding. Flat over a match, or something leaks. */
  get objectCount(): number {
    return this.registry.size
  }

  /**
   * Lights currently in the scene. Worth watching on its own: three.js keys its
   * shader program cache on the light count, so a number that drifts upward is
   * a number that recompiles every lit material in the scene.
   */
  get lightCount(): number {
    let count = 0
    this.scene.traverse((o) => {
      if ((o as { isLight?: boolean }).isLight) count++
    })
    return count
  }

  sweep(alive: ReadonlySet<number>): void {
    for (const id of [...this.registry.keys()]) {
      if (!alive.has(id)) this.remove(id)
    }
  }

  // ────────────────────────────────────────────────────────────── frame ──

  applySettings(settings: Settings): void {
    this.ssao.enabled = settings.ssao
    this.bloom.enabled = settings.bloom
  }

  /**
   * Advances everything time-based in the visuals without drawing.
   *
   * Split from `present` so the headless harness can simulate a second of
   * gameplay and pay for exactly one composited frame — under software
   * rendering, compositing is thousands of times more expensive than the
   * bookkeeping here.
   */
  advance(dt: number, extent: number): void {
    this.applyCamera(dt)
    this.vfx.update(dt, extent)
  }

  /** Draws the current state through the post-processing chain. */
  present(dt: number, elapsed: number): void {
    this.grade.update(elapsed, this.impact)
    this.composer.render(dt)
  }

  render(dt: number, elapsed: number, extent: number): void {
    this.advance(dt, extent)
    this.present(dt, elapsed)
  }

  private resize = (): void => {
    const width = window.innerWidth
    const height = window.innerHeight
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
    this.composer.setSize(width, height)
    this.ssao.setSize(width, height)
    this.bloom.setSize(width, height)

    // The camera fit projects a 27×27 grid through several solver passes, which
    // is far too much to run on every event of a window drag.
    if (this.resizeTimer !== null) clearTimeout(this.resizeTimer)
    this.resizeTimer = window.setTimeout(() => {
      this.resizeTimer = null
      if (this.arenaDef) this.computeBaseDistance(this.arenaDef)
    }, 120)
  }

  dispose(): void {
    window.removeEventListener('resize', this.resize)
    this.renderer.dispose()
  }
}

/**
 * Frees the GPU resources of a scene object.
 *
 * Character rigs share their geometry through the module-level `GEO` table, so
 * for those only the materials may be released; everything else owns both.
 */
function disposeObject(root: Object3D, sharedGeometry: boolean): void {
  root.traverse((node) => {
    const mesh = node as Mesh
    if (!mesh.isMesh && !(node as Points).isPoints) return
    if (!sharedGeometry) mesh.geometry?.dispose()
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    for (const material of materials) material?.dispose()
  })
}

// ────────────────────────────────────────────────────────── effect visuals ──

function createShockwaveVisual(color: number): EffectVisual {
  const object = new Mesh(
    new RingGeometry(0.72, 1, 72),
    new MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.9,
      blending: AdditiveBlending,
      depthWrite: false,
      side: 2,
      toneMapped: false,
    }),
  )
  object.rotation.x = -Math.PI / 2
  object.position.y = 0.12

  return {
    object,
    // `progress` is 0..1 across the ring's life, `intensity` its world radius.
    update(progress: number, intensity: number): void {
      object.scale.setScalar(Math.max(intensity, 0.01))
      ;(object.material as MeshBasicMaterial).opacity = 0.9 * (1 - progress) ** 0.7
    },
  }
}

function createVortexVisual(radius: number, color: number, glow: Texture): EffectVisual {
  const group = new Group()

  const disc = new Mesh(
    new CircleGeometry(radius, 48),
    new MeshBasicMaterial({
      map: glow,
      color: 0xb066ff,
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  disc.rotation.x = -Math.PI / 2
  disc.position.y = 0.08
  group.add(disc)

  const core = new Mesh(
    new TorusGeometry(0.7, 0.12, 8, 32),
    new MeshBasicMaterial({ color, transparent: true, opacity: 0.9, toneMapped: false }),
  )
  core.rotation.x = -Math.PI / 2
  core.position.y = 0.6
  group.add(core)

  const spire = new Mesh(
    new ConeGeometry(0.55, 2.4, 12, 1, true),
    new MeshBasicMaterial({
      color: 0xb066ff,
      transparent: true,
      opacity: 0.35,
      blending: AdditiveBlending,
      depthWrite: false,
      side: 2,
      toneMapped: false,
    }),
  )
  spire.position.y = 1.4
  group.add(spire)

  const light = new PointLight(0xb066ff, 0, 12, 2)
  light.position.y = 1
  group.add(light)

  return {
    object: group,
    update(progress: number, intensity: number, dt: number): void {
      // `intensity` carries the phase: arming ramps up, pulling holds, burst flares.
      core.rotation.z += dt * (2 + intensity * 6)
      spire.rotation.y -= dt * (1.5 + intensity * 5)
      disc.scale.setScalar(0.3 + progress * 0.75)
      ;(disc.material as MeshBasicMaterial).opacity = 0.55 * intensity
      ;(spire.material as MeshBasicMaterial).opacity = 0.35 * intensity
      core.scale.setScalar(0.6 + intensity * 0.7)
      light.intensity = 14 * intensity
    },
  }
}

function createStaticVortexVisual(radius: number, glow: Texture): EffectVisual {
  const group = new Group()

  const disc = new Mesh(
    new CircleGeometry(radius, 56),
    new MeshBasicMaterial({
      map: glow,
      color: 0x5ad6ff,
      transparent: true,
      opacity: 0.22,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  disc.rotation.x = -Math.PI / 2
  disc.position.y = 0.05
  group.add(disc)

  const rings: Mesh[] = []
  for (let i = 0; i < 3; i++) {
    const ring = new Mesh(
      new RingGeometry(radius * (0.3 + i * 0.22), radius * (0.34 + i * 0.22), 64),
      new MeshBasicMaterial({
        color: 0x7fe6ff,
        transparent: true,
        opacity: 0.18,
        blending: AdditiveBlending,
        depthWrite: false,
        side: 2,
        toneMapped: false,
      }),
    )
    ring.rotation.x = -Math.PI / 2
    ring.position.y = 0.07 + i * 0.01
    group.add(ring)
    rings.push(ring)
  }

  return {
    object: group,
    update(_progress: number, _intensity: number, dt: number): void {
      // Counter-rotating rings sell "this space is being pulled inward".
      for (let i = 0; i < rings.length; i++) {
        rings[i].rotation.z += dt * (0.4 + i * 0.35) * (i % 2 === 0 ? 1 : -1)
      }
      disc.rotation.z -= dt * 0.2
    },
  }
}

function createHazardVisual(
  kind: HazardKind,
  radius: number,
  glow: Texture,
): EffectVisual {
  switch (kind) {
    case 'burner':
      return createBurnerVisual(radius, glow, 0x8fff3c, 0xd4ff7a)
    case 'electro':
      return createElectroVisual(radius, glow)
    case 'tornado':
      return createTornadoVisual(radius, glow)
    default:
      return createStaticVortexVisual(radius, glow)
  }
}

/** A funnel of stacked, counter-rotating rings that leans as it travels. */
function createTornadoVisual(radius: number, glow: Texture): EffectVisual {
  const group = new Group()

  const disc = new Mesh(
    new CircleGeometry(radius, 40),
    new MeshBasicMaterial({
      map: glow,
      color: 0xa8b8c8,
      transparent: true,
      opacity: 0.24,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  disc.rotation.x = -Math.PI / 2
  disc.position.y = 0.06
  group.add(disc)

  const rings: Mesh[] = []
  const levels = 7
  for (let i = 0; i < levels; i++) {
    const t = i / (levels - 1)
    // Narrow at the ground, wide at the top: the classic funnel silhouette.
    const r = radius * (0.16 + t * 0.62)
    const ring = new Mesh(
      new TorusGeometry(r, 0.075 + t * 0.05, 6, 22),
      new MeshBasicMaterial({
        color: 0xcfe0ee,
        transparent: true,
        opacity: 0.24 - t * 0.13,
        blending: AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    ring.rotation.x = -Math.PI / 2
    ring.position.y = 0.35 + t * 5.2
    group.add(ring)
    rings.push(ring)
  }

  const light = new PointLight(0xbdd4e8, 2.2, 8, 2)
  light.position.y = 1.6
  group.add(light)

  let spin = 0
  return {
    object: group,
    update(_progress: number, _intensity: number, dt: number): void {
      spin += dt
      for (let i = 0; i < rings.length; i++) {
        const t = i / (rings.length - 1)
        rings[i].rotation.z = spin * (5 - t * 2.5) * (i % 2 === 0 ? 1 : -1)
        // A slight wobble per level makes the funnel look alive, not rigid.
        rings[i].position.x = Math.sin(spin * 2.2 + i) * 0.18 * t
        rings[i].position.z = Math.cos(spin * 1.9 + i) * 0.18 * t
      }
      disc.rotation.z -= dt * 1.2
      light.intensity = 2.2 + Math.sin(spin * 4) * 0.6
    },
  }
}

/** Electro: a flat field that crackles constantly and flashes when it fires. */
function createElectroVisual(radius: number, glow: Texture): EffectVisual {
  const group = new Group()

  const disc = new Mesh(
    new CircleGeometry(radius, 44),
    new MeshBasicMaterial({
      map: glow,
      color: 0x6fc8ff,
      transparent: true,
      opacity: 0.2,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  disc.rotation.x = -Math.PI / 2
  disc.position.y = 0.05
  group.add(disc)

  // Arcs: thin bars in a ring, flickering independently.
  //
  // They used to start at the centre, which meant all nine additive quads
  // overlapped on one point and painted a white hole in the middle of the
  // arena. They now occupy only the outer band, leaving the centre readable.
  const arcs: Mesh[] = []
  for (let i = 0; i < 9; i++) {
    const arc = new Mesh(
      new BoxGeometry(radius * 0.5, 0.05, 0.09),
      new MeshBasicMaterial({
        color: 0xd8f0ff,
        transparent: true,
        opacity: 0.5,
        blending: AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    arc.position.set(0, 0.12, 0)
    arc.rotation.y = (i / 9) * Math.PI * 2
    arc.geometry.translate(radius * 0.68, 0, 0)
    group.add(arc)
    arcs.push(arc)
  }

  // Tight and dim at rest. A 14 m falloff at intensity 2 washed a 28 m circle
  // of floor pale blue and read as a hole in the arena rather than a hazard.
  const light = new PointLight(0x6fc8ff, 0.7, 7, 2)
  light.position.y = 0.7
  group.add(light)

  let t = 0
  return {
    object: group,
    update(progress: number, intensity: number, dt: number): void {
      t += dt
      // Everything here is additive and feeds the bloom pass, so the numbers
      // are small on purpose: an earlier, "obviously safe" set of values turned
      // the whole bridge into a white blob with the fighters lost inside it.
      const charge = Math.pow(progress, 3)
      ;(disc.material as MeshBasicMaterial).opacity = 0.11 + charge * 0.2 + intensity * 0.25

      for (let i = 0; i < arcs.length; i++) {
        const flicker = Math.sin(t * (11 + i * 3.1) + i) * 0.5 + 0.5
        const mat = arcs[i].material as MeshBasicMaterial
        mat.opacity = (0.05 + flicker * 0.13) * (0.5 + charge) + intensity * 0.3
        arcs[i].rotation.y += dt * (i % 2 === 0 ? 0.5 : -0.4)
        arcs[i].scale.x = 0.6 + flicker * 0.5
      }
      light.intensity = 0.7 + charge * 5 + intensity * 16
    },
  }
}

/**
 * The plasma bolt: a bright core, a soft halo and a ground pool that tracks it.
 *
 * The pool matters more than the bolt on an overhead camera — it tells you
 * where the thing actually is on the floor plane, which is what you need when
 * judging whether it will pass in front of or behind someone.
 */
function createBoltVisual(color: number, glow: Texture): EffectVisual {
  const group = new Group()

  // A crescent, not a ball. A ball is a pellet you have to thread through a
  // gap; a crescent is a front that sweeps, which is what a shove should look
  // like and what makes the shot forgiving enough to aim by facing alone.
  // Built lying flat and bowed forward along +Z, the bolt's heading.
  // A boomerang, sized to what it actually hits: the front is 3.4 m across, so
  // the arc has to be too. The first version was a thin 2.1-radian sliver of a
  // 0.95 m ring — on screen a letter C, and something you had to thread rather
  // than sweep with.
  const arc = (inner: number, outer: number, sweep: number): RingGeometry => {
    const geometry = new RingGeometry(inner, outer, 40, 1, -sweep / 2 + Math.PI / 2, sweep)
    geometry.rotateX(-Math.PI / 2)
    geometry.translate(0, 0, -inner * 0.55)
    return geometry
  }

  const core = new Mesh(
    arc(1.5, 2.05, 1.5),
    new MeshBasicMaterial({ color: 0xffffff, toneMapped: false, side: 2 }),
  )
  group.add(core)

  const halo = new Mesh(
    arc(1.25, 2.35, 1.7),
    new MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.5,
      blending: AdditiveBlending,
      depthWrite: false,
      side: 2,
      toneMapped: false,
    }),
  )
  group.add(halo)

  // Deliberately NOT a point light. Every fired bolt would change the scene's
  // light count twice, and three.js keys its shader program cache on that
  // count: each new value recompiles every lit material, which measured at
  // 90–213 ms of blocking `getProgramInfoLog` per program. A bolt is a bright
  // additive sprite instead — on an overhead camera the difference is the
  // faint pool of light it used to cast on the concrete, and it is not worth
  // a stutter every time somebody attacks.
  const pool = new Mesh(
    new PlaneGeometry(4.4, 4.4),
    new MeshBasicMaterial({
      map: glow,
      color,
      transparent: true,
      opacity: 0.55,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  pool.rotation.x = -Math.PI / 2
  // Sits just above the slab regardless of how high the bolt itself is, so it
  // reads as the bolt's shadow-in-reverse and tells you where it will land.
  pool.position.y = -0.8
  group.add(pool)

  let clock = 0

  return {
    object: group,
    update(_progress: number, _intensity: number, dt: number): void {
      // The crescent keeps its heading — it is aimed, so it must not spin.
      clock += dt
      const pulse = 1 + Math.sin(clock * 26) * 0.14
      core.scale.set(pulse, 1, pulse)
      halo.scale.setScalar(pulse)
      pool.scale.setScalar(pulse)
    },
  }
}

/** Floating, spinning collectable with a distinct silhouette per kind. */
function createPickupVisual(kind: PickupKind, glow: Texture): EffectVisual {
  const group = new Group()
  const color = kind === 'shield' ? 0x7fe6ff : 0xffd23c

  const halo = new Mesh(
    new CircleGeometry(1.7, 32),
    new MeshBasicMaterial({
      map: glow,
      color,
      transparent: true,
      opacity: 0.5,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  halo.rotation.x = -Math.PI / 2
  halo.position.y = 0.05
  group.add(halo)

  // A shield reads as a sphere, haste as a spike: silhouette carries the
  // meaning at a glance, colour only confirms it.
  const core = new Mesh(
    kind === 'shield'
      ? new SphereGeometry(0.55, 14, 10)
      : new ConeGeometry(0.48, 1.25, 6),
    new MeshBasicMaterial({ color, transparent: true, opacity: 0.9, toneMapped: false }),
  )
  core.position.y = 1
  group.add(core)

  const cage = new Mesh(
    new TorusGeometry(0.85, 0.07, 6, 20),
    new MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.75,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  cage.position.y = 1
  cage.rotation.x = Math.PI / 2
  group.add(cage)

  const light = new PointLight(color, 8, 9, 2)
  light.position.y = 1
  group.add(light)

  return {
    object: group,
    // `progress` carries the pickup's age in seconds.
    update(progress: number, _intensity: number, dt: number): void {
      core.rotation.y += dt * 1.8
      cage.rotation.z += dt * 2.4
      const bob = Math.sin(progress * 2.4) * 0.16
      core.position.y = 1 + bob
      cage.position.y = 1 + bob
      light.intensity = 8 + Math.sin(progress * 5) * 2
      ;(halo.material as MeshBasicMaterial).opacity = 0.4 + Math.sin(progress * 3) * 0.12
    },
  }
}

function createBurnerVisual(
  radius: number,
  glow: Texture,
  discColor = 0x8fff3c,
  columnColor = 0xd4ff7a,
): EffectVisual {
  const group = new Group()

  const disc = new Mesh(
    new CircleGeometry(radius, 40),
    new MeshBasicMaterial({
      map: glow,
      color: discColor,
      transparent: true,
      opacity: 0.18,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  disc.rotation.x = -Math.PI / 2
  disc.position.y = 0.06
  group.add(disc)

  const column = new Mesh(
    new ConeGeometry(radius * 0.55, 5, 14, 1, true),
    new MeshBasicMaterial({
      color: columnColor,
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
      side: 2,
      toneMapped: false,
    }),
  )
  column.position.y = 2.5
  column.rotation.x = Math.PI
  group.add(column)

  const light = new PointLight(discColor, 0.8, 8, 2)
  light.position.y = 0.8
  group.add(light)

  return {
    object: group,
    update(progress: number, intensity: number, dt: number): void {
      // `progress` is the charge toward the next burst — the telegraph.
      const charge = Math.pow(progress, 3)
      ;(disc.material as MeshBasicMaterial).opacity = 0.14 + charge * 0.34
      disc.scale.setScalar(1 + charge * 0.12)
      light.intensity = 0.8 + charge * 9 + intensity * 20
      ;(column.material as MeshBasicMaterial).opacity = intensity * 0.7
      column.scale.setScalar(0.6 + intensity * 0.6)
      column.rotation.y += dt * 2
    },
  }
}
